/**
 * UrgentSignBanner — Sign Queue urgent strip
 *
 * Red full-width banner at the top of the Sign Queue when there are
 * pending signatures with high urgency. One CTA: "Sign all now".
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (Screen 1)
 * - Conditional: pending tab + signDocs.length > 0
 * - Pure presentation; consumer owns the click handler
 *
 * Rules:
 * - Glass components only (rule 11b)
 * - sx + theme tokens, alpha() for the tinted background (rule 11c)
 * - All copy via t() (rule 13)
 * - 44px touch target on the CTA (portals.md)
 */

import GavelIcon from '@mui/icons-material/Gavel';
import { alpha, Box, GlassButton, iconSizes, Stack, Typography, useI18n, useTheme } from '@ops-matrix/design-system-ui';

export interface UrgentSignBannerProps {
  /** Number of urgent pending signatures (drives the visible count). */
  urgentCount: number;
  /** Earliest deadline copy ("Today, 4:30 PM"). Derived by the parent. */
  deadline?: string;
  /** Optional detail line (defaults to spec-mandated copy). */
  detail?: string;
  /** Loan-context sub-line — e.g. "Marlin Ct loan · 2 documents" */
  loanContext?: string;
  /** Click handler — parent decides whether to launch sign-all or focus first row. */
  onSignAll: () => void;
}

export function UrgentSignBanner({ urgentCount, deadline, detail, loanContext, onSignAll }: UrgentSignBannerProps) {
  const { t } = useI18n();
  const theme = useTheme();

  if (urgentCount <= 0) return null;

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.error.main, 0.1),
        borderLeft: 4,
        borderColor: 'error.main',
      }}
    >
      <GavelIcon sx={{ fontSize: iconSizes.lg, color: 'error.main', flexShrink: 0 }} aria-hidden="true" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" component="p" sx={{ fontWeight: 600, color: 'error.main' }}>
          {deadline
            ? t('consumer.signQueue.urgentStripDeadline', { deadline })
            : t('consumer.signQueue.urgentStripDeadline')}
        </Typography>
        {loanContext && (
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
            {loanContext}
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary">
          {detail ?? t('consumer.signQueue.urgentStripDetail')}
        </Typography>
      </Box>
      <Stack direction="row" sx={{ flexShrink: 0, minHeight: 44 }}>
        <GlassButton
          color="error"
          variant="gradient"
          onClick={onSignAll}
          startIcon={<GavelIcon />}
          sx={{ minHeight: 44 }}
        >
          {t('consumer.signQueue.signAllNow')}
        </GlassButton>
      </Stack>
    </Box>
  );
}

export default UrgentSignBanner;
